import { stripVTControlCharacters } from 'node:util'
/**
 * `stripVTControlCharacters` also removes OSC and other C1 sequences, which a
 * CSI-only regex leaves behind on CLIs that emit terminal hyperlinks.
 * `stripCarriageReturns` additionally flattens the `\r` a progress-spinner child
 * process uses to repaint one line, so line-based filters see a single line.
 */
export function stripAnsi(text: string, options: { stripCarriageReturns?: boolean } = {}): string {
  const stripped = stripVTControlCharacters(text)
  return options.stripCarriageReturns ? stripped.replaceAll('\r', '') : stripped
}

export function toPascalCase(str: string): string {
  return str
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .split(/[-_\s]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join('')
}

export function toCamelCase(str: string): string {
  const p = toPascalCase(str)
  return p.charAt(0).toLowerCase() + p.slice(1)
}

export function singularize(str: string): string {
  if (str.endsWith('ies')) return `${str.slice(0, -3)}y`
  if (str.endsWith('ves')) return `${str.slice(0, -3)}f`
  if (
    str.endsWith('sses') ||
    str.endsWith('xes') ||
    str.endsWith('ches') ||
    str.endsWith('shes') ||
    str.endsWith('zes')
  )
    return str.slice(0, -2)
  if (str.endsWith('s') && !str.endsWith('ss')) return str.slice(0, -1)
  return str
}

export function pluralize(str: string): string {
  if (str.endsWith('s') && !str.endsWith('ss')) return str
  if (str.endsWith('y') && !['ay', 'ey', 'iy', 'oy', 'uy'].some((v) => str.endsWith(v)))
    return `${str.slice(0, -1)}ies`
  if (str.endsWith('s') || str.endsWith('x') || str.endsWith('ch') || str.endsWith('sh'))
    return `${str}es`
  return `${str}s`
}

export function toKebabCase(str: string): string {
  return str
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/[\s_]+/g, '-')
    .toLowerCase()
}

export function toScreamingSnake(str: string): string {
  return str
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .toUpperCase()
}

export function quotePropertyName(name: string): string {
  return /^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(name) ? name : `'${name}'`
}

export function humanizeName(name: string): string {
  return name
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

export function singularizeLabel(label: string): string {
  const words = label.split(' ')
  const lastWord = words[words.length - 1]
  words[words.length - 1] = singularize(lastWord.toLowerCase())
  words[words.length - 1] =
    words[words.length - 1].charAt(0).toUpperCase() + words[words.length - 1].slice(1)
  return words.join(' ')
}

/**
 * Escapes `\r` and `\n` as well as the quote character: a raw newline inside an
 * emitted single- or double-quoted literal is a syntax error, so a schema label
 * containing one would otherwise emit a file that does not parse.
 */
export function quoteLiteral(value: string, quote = "'"): string {
  const escaped = value
    .replace(/\\/g, '\\\\')
    .replace(new RegExp(escapeRegExp(quote), 'g'), `\\${quote}`)
    .replace(/\r/g, '\\r')
    .replace(/\n/g, '\\n')

  return `${quote}${escaped}${quote}`
}

export function escapeTemplateText(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/`/g, '\\`').replace(/\$\{/g, '\\${')
}

export function normalizeLineEndings(content: string): string {
  return content.replace(/\r\n/g, '\n')
}

export function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export function hasConflictMarkers(content: string): boolean {
  return /^(<{7}|={7}|>{7})( |$)/m.test(content)
}

export function splitLines(content: string): string[] {
  return normalizeLineEndings(content).split('\n')
}

export function normalizeWhitespace(value: string): string {
  return value.trim().replace(/\s+/g, ' ')
}

export function escapeJsx(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

export function escapeAttribute(str: string): string {
  return escapeJsx(str).replace(/"/g, '&quot;')
}

export function findNextNonEmptyLine(lines: string[], startIndex: number): string | null {
  for (let i = startIndex; i < lines.length; i++) {
    const trimmed = lines[i]!.trim()
    if (trimmed !== '') return trimmed
  }
  return null
}

export function levenshtein(a: string, b: string): number {
  const matrix = Array.from({ length: a.length + 1 }, () => new Array<number>(b.length + 1).fill(0))

  for (let i = 0; i <= a.length; i++) matrix[i]![0] = i
  for (let j = 0; j <= b.length; j++) matrix[0]![j] = j

  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      matrix[i]![j] = Math.min(
        matrix[i - 1]![j]! + 1,
        matrix[i]![j - 1]! + 1,
        matrix[i - 1]![j - 1]! + cost
      )
    }
  }

  return matrix[a.length]![b.length]!
}

export function toTitleCase(value: string): string {
  return normalizeWhitespace(value)
    .split(' ')
    .map((word) => (word ? `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}` : word))
    .join(' ')
}

export function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-+/g, '-')
}

export function usesIdentifier(source: string, identifier: string): boolean {
  return new RegExp(`\\b${escapeRegExp(identifier)}\\b`).test(source)
}

export function detectIndent(raw: string): string | number {
  const indentMatch = raw.match(/^[ \t]+(?=")/m)
  return indentMatch?.[0] ?? 2
}

export function jsString(value: string): string {
  return JSON.stringify(value)
}

export function safeIdentifier(value: string, fallback: string): string {
  const pascal = toPascalCase(value).replace(/[^A-Za-z0-9_$]/g, '')
  const base = pascal ? `${pascal.charAt(0).toLowerCase()}${pascal.slice(1)}` : fallback
  return /^[A-Za-z_$]/.test(base) ? base : fallback
}
